/*
 * @format
 *
 * Functions to manage schedules
 */

import { daySortIndex, nextDay } from './event';
import {
  dateTimeToMinutesSinceMidnight,
  dateToLongDayString,
  getMinutesSinceSunday,
  getScheduleEventAction,
  convertToSiTime,
  formatTime,
} from '../utils/helpers';
import { DAYS_OF_WEEK, MINUTES_SINCE_SUNDAY } from '../utils/consts';

export const HALO_AIR_SCHEDULE_STEP_SIZE = 10;
export const HALO_SCHEDULE_STEP_SIZE = 15;

export const sortSchedule = (a, b) => {
  const days = daySortIndex(a.day) - daySortIndex(b.day);

  if (days !== 0) {
    return days;
  }

  return a.startTime - b.startTime;
};

const sortByStartTime = (a, b) => a.startTime - b.startTime;

export const weekifySchedule = ([...schedule]) => {
  const nested = schedule.map(s =>
    s.events.map(p => ({
      startTime: MINUTES_SINCE_SUNDAY[s.day] + p.startTime,
      ...getScheduleEventAction(p),
    })),
  );

  const merged = [].concat(...nested);

  return merged.sort(sortByStartTime);
};

const parseEvent = (event, timeDisplayFormat) => {
  if (!event) {
    return '';
  }

  const minutesSinceMidnight = event.startTime % 1440;

  return {
    minutesSinceSunday: event.startTime,
    minutesSinceMidnight,
    displayTime: formatTime(minutesSinceMidnight, timeDisplayFormat),
    day: Object.keys(MINUTES_SINCE_SUNDAY)[Math.floor(event.startTime / 1440)],
    ...getScheduleEventAction(event),
  };
};

export const getEventAt = (schedule, minutesSinceSunday) => {
  if (schedule.length === 0) {
    return undefined;
  }

  const reversedArray = [...schedule].reverse();

  const event =
    reversedArray.find(x => x.startTime <= minutesSinceSunday) ||
    schedule[schedule.length - 1];

  return parseEvent(event);
};

export const getCurrentEvent = schedule => {
  const minutesSinceSunday = getMinutesSinceSunday(convertToSiTime(Date.now()));

  return getEventAt(schedule, minutesSinceSunday);
};

export const getNextEvent = (schedule, plusHoursEndtime, timeDisplayFormat) => {
  if (plusHoursEndtime) {
    const overrideMinutesSinceMidnight =
      dateTimeToMinutesSinceMidnight(plusHoursEndtime);
    const currentDay = dateToLongDayString(plusHoursEndtime);

    const overrideMinutes =
      MINUTES_SINCE_SUNDAY[currentDay] + overrideMinutesSinceMidnight;
    const overrideEvent = getEventAt(schedule, overrideMinutes);

    if (!overrideEvent) {
      return undefined;
    }

    return parseEvent(
      {
        startTime: overrideMinutes,
        setpoint: overrideEvent.setpoint,
        on: overrideEvent.on,
      },
      timeDisplayFormat,
    );
  }

  if (schedule.length === 0) {
    return undefined;
  }

  const minutesSinceSunday = getMinutesSinceSunday(convertToSiTime(Date.now()));

  // if no event is found it is the last event of the week
  // and should rollover to the first
  const nextEvent =
    schedule.find(x => x.startTime > minutesSinceSunday) || schedule[0];

  return parseEvent(nextEvent, timeDisplayFormat);
};

export const getEventProgress = schedule => {
  const minutesSinceSunday = getMinutesSinceSunday(convertToSiTime(Date.now()));

  const currentEvent = getEventAt(schedule, minutesSinceSunday);
  // don't pass override endtime as we don't want it included in the schedule progress
  const nextEvent = getNextEvent(schedule, undefined);

  if (!currentEvent || !nextEvent) {
    return 0;
  }

  const diff = Math.abs(
    nextEvent.minutesSinceSunday - currentEvent.minutesSinceSunday,
  );
  const current = minutesSinceSunday - currentEvent.minutesSinceSunday;

  return (current / diff) * 100;
};

export const getEventsForDay = (schedule, day) => {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }

  return schedule
    .sort(sortSchedule)
    .reduce((acc, curr, idx, src) => {
      let endTime;
      if (idx + 1 > src.length - 1) {
        endTime = src[0].startTime;
      } else {
        endTime = src[idx + 1].startTime;
      }

      acc.push({
        ...curr,
        endTime,
      });
      return acc;
    }, [])
    .filter(x => x.day === day);
};

export const flattenSchedule = ([...schedule]) => {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }

  if (!(schedule.length === 0)) {
    if (!Array.isArray(schedule[0].events)) {
      throw Error(
        "It looks like you've flattened this object already. If this is wrong, check the object you're passing in for an 'events' array",
      );
    }
  }
  const nested = schedule.map(s =>
    s.events.map(event => ({
      day: s.day,
      startTime: event.startTime,
      ...getScheduleEventAction(event),
    })),
  );

  const merged = [].concat(...nested);

  return merged.sort(sortSchedule);
};

export const unflattenSchedule = ([...schedule]) => {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }
  if (Array.isArray(schedule[0].events)) {
    throw Error(
      "It looks like you've unflattened this object already. If this is wrong, check the object you're passing in for an 'events' array",
    );
  }

  return schedule.reduce((acc, curr) => {
    let day = acc.find(d => d.day === curr.day);

    if (!day) {
      day = {
        day: curr.day,
        events: [],
      };

      acc.push(day);
    }

    day.events.push({
      startTime: curr.startTime,
      ...getScheduleEventAction(curr),
    });

    return acc;
  }, []);
};

export const findEventInDay = (schedule, day, startTime) => {
  return schedule.findIndex(x => x.day === day && x.startTime === startTime);
};

export const findNextEventIndexInDay = (schedule, day, startTime) => {
  return schedule.findIndex(x => x.day === day && x.startTime > startTime);
};

export const findPreviousEventIndexInDay = (schedule, day, startTime) => {
  return schedule.findIndex(x => x.day === day && x.startTime < startTime);
};

export const getUsedStartTimes = (day, scheduleEvents) =>
  scheduleEvents.filter(x => x.day === day).map(x => x.startTime);

export const defaultStartTime = (usedStartTimes, incrementSize = 15) => {
  const twelveHoursInMinutes = 720;

  if (!usedStartTimes) {
    return twelveHoursInMinutes;
  }

  let startTime = twelveHoursInMinutes;

  for (let i = 0; i < usedStartTimes.length; i + 1) {
    if (!usedStartTimes.includes(startTime)) {
      return startTime;
    }

    startTime += incrementSize;
  }

  return startTime;
};

export const defaultEndTime = (schedule, day, startTime) => {
  const sortedSchedule = [...schedule].sort(sortSchedule);

  const nextEventIndex = findNextEventIndexInDay(
    sortedSchedule,
    day,
    startTime,
  );
  const nextEventInDay = schedule[nextEventIndex];

  if (!nextEventInDay) {
    const nextDayInSchedule = nextDay(day);
    const nextDaysEvents = sortedSchedule.filter(event => {
      return event.day === nextDayInSchedule;
    });

    return nextDaysEvents[0].startTime;
  }

  return nextEventInDay.startTime;
};

export const finalEndTime = (schedule, day) => {
  if (!schedule || !day) {
    throw new Error('No schedule or day specified');
  }

  const sortedSchedule = [...schedule].sort(sortSchedule);

  const nextDayInSchedule = nextDay(day);
  const nextDaysEvents = sortedSchedule.filter(event => {
    return event.day === nextDayInSchedule;
  });

  return nextDaysEvents[0].startTime;
};

export const endTimeLimit = (schedule, day, startTime) => {
  if (!schedule || !day || !startTime) {
    throw new Error('No schedule, day or startTime specified');
  }

  const sortedSchedule = [...schedule].sort(sortSchedule);

  const eventsLaterThanStartTime = sortedSchedule.filter(event => {
    return event.day === day && event.startTime > startTime;
  });

  if (!eventsLaterThanStartTime[1]) {
    return finalEndTime(sortedSchedule, day);
  }

  return eventsLaterThanStartTime[1].startTime;
};

export const addEvent = (schedule, event, isHpiAddEvent = false) => {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }

  const safeEvent = {
    day: event.day,
    startTime: event.startTime,
    ...getScheduleEventAction(event),
  };

  if (schedule.length === 0) {
    return [safeEvent];
  }

  // copy the array so we don't mutate the old schedule
  const mutableSched = [...schedule].sort(sortSchedule);

  if (event.endTime) {
    // find next event after start time
    const nextEventIndex = findNextEventIndexInDay(
      mutableSched,
      event.day,
      event.startTime,
    );

    if (nextEventIndex < 0) {
      // if the nextEventIndex is less than 0 then the event
      // being added is the new last event of the day

      const specifiedDayEndTimeIndex = findEventInDay(
        mutableSched,
        nextDay(event.day),
        finalEndTime(mutableSched, event.day),
      );

      // adding the new event but not updating any other events
      mutableSched.splice(specifiedDayEndTimeIndex, 0, safeEvent);

      return mutableSched.sort(sortSchedule);
    }

    if (!isHpiAddEvent) {
      const nextEvent = mutableSched[nextEventIndex];

      // remove the next event, and re-add with new start time
      mutableSched.splice(nextEventIndex, 1, safeEvent, {
        day: nextEvent.day,
        startTime: event.endTime,
        ...getScheduleEventAction(nextEvent),
      });

      // add affected events
      return mutableSched.sort(sortSchedule);
    }
  }

  // add affected events
  return schedule.concat([safeEvent]).sort(sortSchedule);
};

export const deleteEvent = (schedule, event) => {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }

  const mutableSched = schedule.filter(
    s => !(s.day === event.day && s.startTime === event.startTime),
  );

  return mutableSched.sort(sortSchedule);
};

export const editEvent = (schedule, oldEvent, newEvent) => {
  const mutableSched = deleteEvent(schedule, oldEvent);
  return addEvent(mutableSched, newEvent);
};

export function editPairedEvent(
  schedule,
  oldFirstEvent,
  oldSecondEvent,
  newFirstEvent,
  newSecondEvent,
) {
  if (!Array.isArray(schedule)) {
    throw Error('Schedule was not an array');
  }

  if (!oldFirstEvent || !oldSecondEvent || !newFirstEvent || !newSecondEvent) {
    console.log('Missing event', {
      oldFirstEvent,
      oldSecondEvent,
      newFirstEvent,
      newSecondEvent,
    });
    throw Error('Missing event');
  }

  // delete the start and end event
  const clearedSchedule = deleteEvent(
    deleteEvent(schedule, oldFirstEvent),
    oldSecondEvent,
  );

  const updatedSchedule = addEvent(
    addEvent(clearedSchedule, newFirstEvent),
    newSecondEvent,
  );

  return updatedSchedule;
}

function minimumStartTimeForEditingEvent(
  schedule,
  day,
  existingStartTime,
  isHpi,
) {
  const previousEvent = schedule
    .filter(x => x.day === day)
    .reverse() // only reversing the output of filter
    .find(x => existingStartTime > x.startTime);

  const minTime = previousEvent
    ? previousEvent.startTime +
      (isHpi ? HALO_AIR_SCHEDULE_STEP_SIZE : HALO_SCHEDULE_STEP_SIZE)
    : 0;

  return minTime;
}

function maximumStartTimeForEditingEvent(endTime, isHpi) {
  return (
    endTime - (isHpi ? HALO_AIR_SCHEDULE_STEP_SIZE : HALO_SCHEDULE_STEP_SIZE)
  );
}

export function editStartTimeRange(
  schedule,
  day,
  endTime,
  existingStartTime,
  existingEndTime,
  isHpi,
) {
  const isLast = isLastEventOfDay(schedule, day, existingStartTime);

  return [
    {
      min: minimumStartTimeForEditingEvent(
        schedule,
        day,
        existingStartTime,
        isHpi,
      ),
      max: isLast
        ? isHpi
          ? 1430
          : 1425
        : maximumStartTimeForEditingEvent(endTime, isHpi),
    },
  ];
}

function maximumEndTimeForEditingEvent(scheduleEvents, day, endTime, isHpi) {
  const previousEvent = scheduleEvents
    .filter(x => x.day === day)
    .find(x => x.startTime > endTime);

  const maxTime = previousEvent
    ? previousEvent.startTime -
      (isHpi ? HALO_AIR_SCHEDULE_STEP_SIZE : HALO_SCHEDULE_STEP_SIZE)
    : isHpi
    ? 1430
    : 1425;

  return maxTime;
}

export function isLastEventOfDay(schedule, day, startTime) {
  const laterEvents = schedule.filter(
    x => x.day === day && x.startTime > startTime,
  );

  return laterEvents.length === 0;
}

export function editEndTimeRange(
  schedule,
  day,
  selectedStartTime,
  existingStartTime,
  existingEndTime,
  isHpi,
) {
  const isLast = isLastEventOfDay(schedule, day, existingStartTime);

  return [
    {
      min:
        selectedStartTime +
        (isHpi ? HALO_AIR_SCHEDULE_STEP_SIZE : HALO_SCHEDULE_STEP_SIZE),
      max: isLast
        ? isHpi
          ? 1430
          : 1425
        : maximumEndTimeForEditingEvent(schedule, day, existingEndTime, isHpi),
    },
  ];
}

export function areSelectionsValid({
  scheduleEvents,
  day,
  startTime,
  endTime,
  isHpi,
}) {
  if (isHpi === undefined) {
    throw new Error('Please specify the heating type');
  }

  const events = getEventsForDay(scheduleEvents, day);

  if (startTime === undefined || endTime === undefined) {
    return false;
  }

  const isDhwSchedule = events.includes(x => x.on !== undefined);

  if (!(isHpi || isDhwSchedule)) {
    return true;
  }

  const clash = events.some(
    e => startTime === e.startTime || endTime === e.startTime,
  );

  if (clash) {
    return false;
  }

  // are there any events between this comfort and the reduced time
  const overlappingEvents = events.some(
    e => startTime < e.startTime && endTime > e.startTime,
  );

  if (overlappingEvents) {
    return false;
  }
  // would the addition cause a mismatch of pairs
  // is the event before the comfort also a comfort?
  // is the event before the reduced also a reduced?

  const reversedEvents = [...events].reverse();
  const beforeStartEvent = reversedEvents.find(e => e.startTime <= startTime);

  if (isDhwSchedule) {
    return handlePreviousDhwEvent(events, beforeStartEvent, startTime, endTime);
  }

  return isHpi
    ? handlePreviousHeatingEvent(events, beforeStartEvent, startTime, endTime)
    : true;
}

const handlePreviousHeatingEvent = (
  events,
  previousEvent,
  startTime,
  endTime,
) => {
  if (previousEvent?.setpoint === 'comfort') {
    return false;
  }

  // taking into account the new comfort event that might be added
  const reversedEventsIncludingComfort = [
    ...events,
    { startTime: startTime, setpoint: 'comfort' },
  ].reverse();
  const beforeReduced = reversedEventsIncludingComfort.find(
    e => e.startTime <= endTime,
  );

  if (beforeReduced?.setpoint === 'reduced') {
    return false;
  }

  return true;
};

const handlePreviousDhwEvent = (events, previousEvent, startTime, endTime) => {
  if (previousEvent?.on === true) {
    return false;
  }

  // taking into account the new comfort event that might be added
  const reversedEventsIncludingComfort = [
    ...events,
    { startTime: startTime, on: true },
  ].reverse();
  const beforeReduced = reversedEventsIncludingComfort.find(
    e => e.startTime <= endTime,
  );

  if (beforeReduced?.on === false) {
    return false;
  }

  return true;
};

export const HEATPUMP_SCHEDULE_STEP_SIZE = 10;
export const CLASSIC_SCHEDULE_STEP_SIZE = 15;
const minuteLimit = 1440;
const minimumScheduleSize = HEATPUMP_SCHEDULE_STEP_SIZE * 2;

export function availableStartTimeRanges({ day, scheduleEvents, isHpi }) {
  if (isHpi === undefined) {
    throw new Error('Please specify the heating type');
  }

  if (!isHpi) {
    return availableStartTimeRangesHalo({ scheduleEvents, day });
  }

  // returns arrays of min/max minutes since midnight [{ min: 0, max: 10}]
  const step = isHpi ? HEATPUMP_SCHEDULE_STEP_SIZE : CLASSIC_SCHEDULE_STEP_SIZE;
  const minimumEventSize = isHpi
    ? minimumScheduleSize
    : CLASSIC_SCHEDULE_STEP_SIZE;
  const events = getEventsForDay(scheduleEvents, day);

  const result = [];

  if (events.length > 0) {
    let previousStartTime = 0;

    for (let i = 0; i < events.length; i += 2) {
      result.push({
        min: previousStartTime,
        max: events[i + 0].startTime - minimumEventSize,
      });

      previousStartTime = events[i + 1].startTime + step;
    }

    const lastEvent = events[events.length - 1];
    result.push({
      min: lastEvent.startTime + step,
      max: minuteLimit - minimumEventSize,
    });
  } else {
    result.push({ min: 0, max: minuteLimit - minimumEventSize });
  }

  // remove events that are shorter than the minimum
  return result.filter(x => x.max - x.min > minimumEventSize);
}

export function availableStartTimeRangesHalo({ scheduleEvents, day }) {
  const events = getEventsForDay(scheduleEvents, day);

  if (events.length === 0) {
    return [{ min: 0, max: 1425 }];
  }

  const eventSlots = events.map(x => x.startTime);

  const result = [];
  let currentSequence = null;
  for (let i = 0; i <= 1425; i += CLASSIC_SCHEDULE_STEP_SIZE) {
    if (eventSlots.includes(i)) {
      if (currentSequence !== null) {
        result.push({
          min: currentSequence[0],
          max: currentSequence[1],
        });
        currentSequence = null;
      }
    } else {
      if (currentSequence === null) {
        currentSequence = [i];
      }

      currentSequence[1] = i;
    }
  }

  if (currentSequence !== null) {
    result.push({
      min: currentSequence[0],
      max: 1425,
    });
  }

  return result;
}

export function availableEndTimeRanges({
  day,
  scheduleEvents,
  startTime,
  isHpi,
}) {
  if (isHpi === undefined) {
    throw new Error('Please specify the heating type');
  }
  const limit = isHpi ? 1430 : 1425;
  const stepSize = isHpi ? 10 : 15;

  const previousEvent = scheduleEvents
    .filter(x => x.day === day)
    .find(x => x.startTime > startTime);

  const maxTime = previousEvent ? previousEvent.startTime - stepSize : limit;

  return [{ min: startTime + stepSize, max: maxTime }];
}

// The Add event for a Halo Heating has special logic for the end time
// It is editing the start of the next event, so the end time can range from just after the start time, up to the event after that
export function availableEndTimeRangesAddHaloHeating({
  day,
  scheduleEvents,
  startTime,
}) {
  const limit = 1425;
  const stepSize = CLASSIC_SCHEDULE_STEP_SIZE;

  // find the event we're editing, it might be there isn't one (as it's really tomorrow)
  const existingEvent = scheduleEvents
    .filter(x => x.day === day)
    .find(x => x.startTime > startTime);

  // if we did find an existing event, find the one after that
  const nextEvent = existingEvent
    ? scheduleEvents
        .filter(x => x.day === day)
        .find(x => x.startTime > existingEvent.startTime)
    : undefined;

  const maxTime = nextEvent ? nextEvent.startTime - stepSize : limit;

  return [{ min: startTime + stepSize, max: maxTime }];
}

export function minimumComfortTime({ day, scheduleEvents, comfortStartTime }) {
  const previousEvent = scheduleEvents
    .filter(x => x.day === day)
    .reverse() // only reversing the output of filter
    .find(x => comfortStartTime > x.startTime);

  const minTime = previousEvent
    ? previousEvent.startTime + HEATPUMP_SCHEDULE_STEP_SIZE
    : 0;

  return minTime;
}

export function maxiumumComfortTime({ reducedStartTime }) {
  return reducedStartTime - HEATPUMP_SCHEDULE_STEP_SIZE;
}

export function minimumReducedTime({ comfortStartTime }) {
  return comfortStartTime + HALO_AIR_SCHEDULE_STEP_SIZE;
}

export function maximumReducedTime({ day, scheduleEvents, reducedStartTime }) {
  const previousEvent = scheduleEvents
    .filter(x => x.day === day)
    .find(x => x.startTime > reducedStartTime);

  const maxTime = previousEvent
    ? previousEvent.startTime - HEATPUMP_SCHEDULE_STEP_SIZE
    : 1430;

  return maxTime;
}

export function prepareDeletedSchedule({
  scheduleEvents,
  day,
  existingStartTime,
  existingEndTime,
  isHpi,
}) {
  if (isHpi === undefined) {
    throw new Error('Please specify the heating type');
  }

  if (isHpi) {
    const step1 = deleteEvent(scheduleEvents, {
      day,
      startTime: existingStartTime,
    });
    return deleteEvent(step1, {
      day,
      startTime: existingEndTime,
    });
  } else {
    return deleteEvent(scheduleEvents, {
      day,
      startTime: existingStartTime,
    });
  }
}

export const daysOfWeek = Object.values(DAYS_OF_WEEK).map((name, index) => ({
  name,
  index,
}));
